// screens/receptionist/WalkInAppointmentScreen.jsx
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  lookupPatient,
  resetLookup,
  createWalkInAppointment,
  fetchDepartments,
  fetchDoctorsByDepartment,
  searchPatients,
} from "../../Redux/actions/receptionistActions/receptionistActions";
import { showToast } from "../../components/ToastMessage";
import { printWalkInReceipt } from "../../components/receptionist/PrintWalkInReceipt";
import Loading from "../../components/Loading";

const WalkInAppointmentScreen = () => {
  const handleReset = () => {
    setFormData({
      email: "",
      mrCode: "",
      pName: "",
      password: "",
      fatherName: "",
      age: "",
      address: "",
      contact: "",
      department: "",
      doctor: "",
      date: new Date().toISOString().split("T")[0],
      time: "",
      bp: "",
      weight: "",
      spo2: "",
      temperature: "",
      height: "",
      bmi: "",
      priority: "Normal",
      waitingTime: 30,
      feePaid: "",
      paymentMethod: "cash",
    });
    setSearchValue("MR-");
    setIsExisting(null);
    dispatch(resetLookup());
  };
  const dispatch = useDispatch();
  const { patient, departments, doctors, loading } = useSelector(
    (state) => state.receptionist
  );

  const [searchValue, setSearchValue] = useState("MR-");
  const [isExisting, setIsExisting] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    mrCode: "",
    pName: "",
    password: "",
    fatherName: "",
    age: "",
    address: "",
    contact: "",
    department: "",
    doctor: "",
    date: new Date().toISOString().split("T")[0],
    time: "",
    bp: "",
    weight: "",
    spo2: "",
    temperature: "",
    height: "",
    bmi: "",
    priority: "Normal",
    waitingTime: 30,
    feePaid: "",
    paymentMethod: "cash",
  });

  useEffect(() => {
    dispatch(fetchDepartments());
  }, [dispatch]);

  useEffect(() => {
    if (formData.department) {
      dispatch(fetchDoctorsByDepartment(formData.department));
    }
  }, [formData.department, dispatch]);

  // Auto-calculate BMI
  useEffect(() => {
    if (formData.weight && formData.height) {
      const heightInMeters = formData.height / 100;
      const bmi = (formData.weight / (heightInMeters * heightInMeters)).toFixed(
        1
      );
      setFormData((prev) => ({ ...prev, bmi }));
    }
  }, [formData.weight, formData.height]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  const suggestionsRef = useRef(null);
  // ✅ ADD THIS useEffect FOR CLICK OUTSIDE (after existing useEffects)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // OPTIONAL → When user leaves input empty, auto insert MR-
  const handleBlur = () => {
    if (searchValue.trim() === "") {
      setSearchValue("MR-");
    }
  };
  // ✅ ADD THIS NEW FUNCTION (after handleBlur function)
  const handleSearchChange = async (value) => {
    setSearchValue(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If empty or just "MR-", hide suggestions
    if (!value || value === "MR-" || value.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // If it's an email or MR code format, don't show suggestions
    if (value.includes("@") || value.startsWith("MR-")) {
      setShowSuggestions(false);
      return;
    }

    // Debounce search
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      const res = await dispatch(searchPatients(value));
      if (res.ok) {
        setSearchSuggestions(res.patients);
        setShowSuggestions(res.patients.length > 0);
      }
      setIsSearching(false);
    }, 300); // 300ms debounce
  };

  const handleSelectPatient = (patient) => {
    setSearchValue(patient.mrCode);
    setShowSuggestions(false);
    setSearchSuggestions([]);
    setIsExisting(true);

    setFormData((prev) => ({
      ...prev,
      email: patient.email || "",
      mrCode: patient.mrCode || "",
      pName: patient.name || "",
      password: "******",
      contact: patient.contact || "",
      fatherName: patient.fatherName || "",
      age: patient.age || "",
      address: patient.address || "",
      bp: patient.vitals?.bp || "",
      weight: patient.vitals?.weight || "",
      spo2: patient.vitals?.spo2 || "",
      temperature: patient.vitals?.temperature || "",
      height: patient.vitals?.height || "",
      bmi: patient.vitals?.bmi || "",
    }));

    showToast(`✅ Patient ${patient.name} selected`);
  };
  const handleClearSearch = () => {
    setSearchValue("MR-");
    setSearchSuggestions([]);
    setShowSuggestions(false);
    setIsExisting(null);
    dispatch(resetLookup());
  };
  // WalkInAppointmentScreen.jsx - handleLookup function
  const handleLookup = async () => {
    if (!searchValue) {
      showToast("Enter Email or MR-Code");
      return;
    }

    const res = await dispatch(lookupPatient(searchValue));

    if (res.ok) {
      setIsExisting(true);

      setFormData((prev) => ({
        ...prev,
        // Patient basic info
        email: res.patient?.email || "",
        mrCode: res.patient?.mrCode || "",
        pName: res.patient?.name || "",
        password: "******",

        // Appointment info from last appointment
        contact: res.aptExist?.contact || "",
        fatherName: res.aptExist?.fatherName || "",
        age: res.aptExist?.age || "",
        address: res.aptExist?.address || "",

        // ✅ Vitals from last appointment
        bp: res.aptExist?.vitals?.bp || "",
        weight: res.aptExist?.vitals?.weight || "",
        spo2: res.aptExist?.vitals?.spo2 || "",
        temperature: res.aptExist?.vitals?.temperature || "",
        height: res.aptExist?.vitals?.height || "",
        bmi: res.aptExist?.vitals?.bmi || "",
      }));

      // Show appropriate message
      const hasVitals =
        res.aptExist?.vitals?.bp || res.aptExist?.vitals?.weight;
      if (hasVitals) {
        showToast("✅ Patient found - vitals loaded from last visit");
      } else {
        showToast("✅ Patient found - please enter vitals");
      }
    } else {
      setIsExisting(false);
      dispatch(resetLookup());
      showToast("New patient - fill form");
      handleReset();
      // Pre-fill email if searched by email
      if (searchValue.includes("@")) {
        setFormData((prev) => ({ ...prev, email: searchValue }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (
      !formData.pName ||
      !formData.department ||
      !formData.doctor ||
      !formData.date ||
      !formData.time ||
      formData.feePaid === "" ||
      formData.feePaid === null ||
      formData.feePaid === undefined ||
      !formData.email
    ) {
      showToast("Fill all required fields");
      return;
    }

    if (!isExisting && (!formData.email || !formData.password)) {
      showToast("Email and password required for new patients");
      return;
    }

    const res = await dispatch(createWalkInAppointment(formData));
    if (res.ok) {
      showToast("Appointment Booked Successfully");

      // Print receipt
      printWalkInReceipt(res.appointment);

      handleReset();
      setSearchValue("MR-");
      setIsExisting(null);
      dispatch(resetLookup());
    } else {
      showToast(res.error);
    }
  };

  return (
    <div
      className="container-fluid"
      style={{ background: "#f8f9fa", minHeight: "100vh", padding: "2rem" }}
    >
      {/* Header
      <div className="row mb-4">
        <div className="col-12">
          <div style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            padding: "1.5rem",
            borderRadius: "12px",
            color: "white",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
          }}>
            <h3 className="mb-0 fw-bold">Walk-In Appointment</h3>
            <p className="mb-0" style={{ opacity: 0.9 }}>Register new patient or book existing patient</p>
          </div>
        </div>
      </div> */}

      {/* Patient Lookup */}
      <div className="row mb-4">
        <div className="col-12">
          <div
            className="card"
            style={{
              borderRadius: "12px",
              border: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div className="card-body p-4">
              <h5 className="fw-semibold mb-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  className="me-2"
                  style={{ color: "#667eea" }}
                >
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                </svg>
                Step 1: Lookup Patient
              </h5>
              <div className="row align-items-end">
                <div className="col-md-8">
                  <label className="form-label">
                    Enter Patient Name, Email or MR-Code
                  </label>
                  <div style={{ position: "relative" }} ref={suggestionsRef}>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      placeholder="e.g. John Doe, patient@email.com or MR-00001"
                      value={searchValue}
                      onBlur={handleBlur}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleLookup()}
                      style={{ borderRadius: "8px", paddingRight: "40px" }}
                    />

                    {/* Clear button */}
                    {searchValue && searchValue !== "MR-" && (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "5px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#6c757d",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                          <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                        </svg>
                      </button>
                    )}

                    {/* Search Suggestions Dropdown */}
                    {showSuggestions && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          background: "white",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          marginTop: "5px",
                          maxHeight: "300px",
                          overflowY: "auto",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          zIndex: 1000,
                        }}
                      >
                        {isSearching ? (
                          <div className="p-3 text-center text-muted">
                            Searching...
                          </div>
                        ) : searchSuggestions.length > 0 ? (
                          searchSuggestions.map((patient) => (
                            <div
                              key={patient._id}
                              onClick={() => handleSelectPatient(patient)}
                              style={{
                                padding: "12px 16px",
                                cursor: "pointer",
                                borderBottom: "1px solid #f0f0f0",
                                transition: "background 0.2s",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f8f9fa")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              <div style={{ fontWeight: "600", color: "#333" }}>
                                {patient.name}
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "0.85em",
                                    color: "#667eea",
                                    fontWeight: "500",
                                  }}
                                >
                                  {patient.mrCode}
                                </span>
                              </div>
                              <div
                                style={{
                                  fontSize: "0.85em",
                                  color: "#666",
                                  marginTop: "4px",
                                }}
                              >
                                {patient.fatherName &&
                                  `S/O ${patient.fatherName}`}
                                {patient.age && ` • ${patient.age} years`}
                                {patient.address &&
                                  ` • ${patient.address.substring(0, 40)}${
                                    patient.address.length > 40 ? "..." : ""
                                  }`}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-3 text-center text-muted">
                            No patients found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <button
                    className="btn btn-primary btn-lg w-100"
                    onClick={handleLookup}
                    disabled={loading}
                    style={{ borderRadius: "8px" }}
                  >
                    {loading ? "Searching..." : "Search Patient"}
                  </button>
                </div>
              </div>

              {isExisting !== null && (
                <div
                  className={`alert ${
                    isExisting ? "alert-success" : "alert-info"
                  } mt-3 mb-0`}
                  style={{ borderRadius: "8px" }}
                >
                  {isExisting ? (
                    <>
                      <strong>✓ Patient Found:</strong> {patient?.patient.name}{" "}
                      ({patient?.patient.mrCode})
                    </>
                  ) : (
                    <>
                      <strong>ℹ New Patient:</strong> Please fill the form below
                      to register
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Left Column - Patient Info */}
          <div className="col-lg-6 mb-4">
            <div
              className="card h-100"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <div className="card-body p-4">
                <h5 className="fw-semibold mb-4">Patient Information</h5>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isExisting}
                      required
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      MR-Code {isExisting && "*"}
                    </label>
                    <input
                      type="text"
                      name="mrCode"
                      className="form-control"
                      value={formData.mrCode}
                      disabled
                      placeholder="Auto-generated"
                      style={{ borderRadius: "8px", background: "#f5f5f5" }}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Patient Name *</label>
                    <input
                      type="text"
                      name="pName"
                      className="form-control"
                      value={formData.pName}
                      onChange={handleChange}
                      disabled={isExisting}
                      required
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Password *</label>
                    <input
                      type="text"
                      name="password"
                      className="form-control"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={isExisting}
                      placeholder={
                        isExisting ? "Existing user" : "Enter password"
                      }
                      required={!isExisting}
                      style={{
                        borderRadius: "8px",
                        background: isExisting ? "#f5f5f5" : "white",
                      }}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Father Name</label>
                    <input
                      type="text"
                      name="fatherName"
                      className="form-control"
                      value={formData.fatherName}
                      onChange={handleChange}
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Age</label>
                    <input
                      type="text"
                      name="age"
                      className="form-control"
                      value={formData.age}
                      onChange={handleChange}
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Contact</label>
                    <input
                      type="text"
                      name="contact"
                      className="form-control"
                      value={formData.contact}
                      onChange={handleChange}
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Address</label>
                    <textarea
                      name="address"
                      className="form-control"
                      rows="2"
                      value={formData.address}
                      onChange={handleChange}
                      style={{ borderRadius: "8px" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Appointment & Vitals */}
          <div className="col-lg-6 mb-4">
            {/* Appointment Details */}
            <div
              className="card mb-3"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <div className="card-body p-4">
                <h5 className="fw-semibold mb-4">Appointment Details</h5>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Department *</label>
                    <select
                      name="department"
                      className="form-select"
                      value={formData.department}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="">Select Department*</option>
                      {departments.map((dept) => (
                        <option key={dept._id} value={dept.name}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Doctor *</label>
                    <select
                      name="doctor"
                      className="form-select"
                      value={formData.doctor}
                      onChange={handleChange}
                      required
                      disabled={!formData.department}
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="">Select Doctor</option>
                      {doctors.map((doc) => (
                        <option key={doc._id} value={doc._id}>
                          Dr. {doc.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Date *</label>
                    <input
                      type="date"
                      name="date"
                      className="form-control"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Time *</label>
                    <input
                      type="time"
                      name="time"
                      className="form-control"
                      value={formData.time}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Priority *</label>
                    <select
                      name="priority"
                      className="form-select"
                      value={formData.priority}
                      onChange={handleChange}
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="Normal">Normal</option>
                      <option value="Urgent">Urgent</option>
                      <option value="Most Urgent">Most Urgent</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Waiting Time (minutes) *
                    </label>
                    <input
                      type="number"
                      name="waitingTime"
                      className="form-control"
                      value={formData.waitingTime}
                      onChange={handleChange}
                      min="0"
                      placeholder="30"
                      style={{ borderRadius: "8px" }}
                    />
                    <small className="text-muted">
                      Estimated waiting time for patient
                    </small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Fee Paid (Rs)</label>
                    <input
                      type="number"
                      name="feePaid"
                      className="form-control"
                      value={formData.feePaid}
                      required
                      onChange={handleChange}
                      placeholder="0"
                      style={{ borderRadius: "8px" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Vitals */}
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <div className="card-body p-4">
                <h5 className="fw-semibold mb-4">Vital Signs</h5>

                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">BP</label>
                    <input
                      type="text"
                      name="bp"
                      className="form-control"
                      value={formData.bp}
                      onChange={handleChange}
                      placeholder="120/80"
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Weight (kg)</label>
                    <input
                      type="number"
                      name="weight"
                      className="form-control"
                      value={formData.weight}
                      onChange={handleChange}
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">SPO2 (%)</label>
                    <input
                      type="number"
                      name="spo2"
                      className="form-control"
                      value={formData.spo2}
                      onChange={handleChange}
                      max="100"
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Temp (°F)</label>
                    <input
                      type="number"
                      name="temperature"
                      className="form-control"
                      value={formData.temperature}
                      onChange={handleChange}
                      step="0.1"
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Height (cm)</label>
                    <input
                      type="number"
                      name="height"
                      className="form-control"
                      value={formData.height}
                      onChange={handleChange}
                      style={{ borderRadius: "8px" }}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">BMI</label>
                    <input
                      type="text"
                      name="bmi"
                      className="form-control"
                      value={formData.bmi}
                      disabled
                      placeholder="Auto"
                      style={{ borderRadius: "8px", background: "#f5f5f5" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <div className="d-flex gap-3 justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary btn-lg"
                onClick={handleReset}
                style={{ borderRadius: "10px", minWidth: "150px" }}
              >
                Reset Form
              </button>
              <button
                type="submit"
                className="btn btn-success btn-lg"
                disabled={loading}
                style={{ borderRadius: "10px", minWidth: "200px" }}
              >
                {loading ? "Creating..." : "Create Appointment"}
              </button>
            </div>
          </div>
        </div>
      </form>

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Loading />
        </div>
      )}
    </div>
  );
};

export default WalkInAppointmentScreen;
